import React, { useState, useEffect, useMemo, useCallback } from "react";
import MatchingItem from "components/repair/matchingItem";
import FilterListMenu from "components/repair/filterListMenu";
import { getMyMatchesApi } from "api/match";
import RequestDialog from "components/repair/request_dialog";
import { useUser } from "util/context/user_context";
import { useSearchParams, useLocation, useNavigationType } from "react-router-dom";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber800: "#92400E",
  blue700: "#1D4ED8",
};

const ITEMS_PER_PAGE = 5;
const PAGE_GROUP = 5;

function StatChip({ label, value, accent }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "14px 18px",
        background: "#fff",
        borderRadius: 12,
        border: `1px solid ${C.line}`,
        minWidth: 120,
        flex: "0 1 auto",
      }}
    >
      <div style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>{label}</div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: accent || C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: 20,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 12,
          background: "#F3F4F6",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 18, width: "60%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "40%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "50%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        background: "#fff",
        borderRadius: 16,
        border: `1px dashed ${C.line}`,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 9999,
          background: C.greenBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 34,
        }}
      >
        🔧
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
        조건에 맞는 요청이 없어요
      </div>
      <div style={{ fontSize: 13, color: C.sub, textAlign: "center" }}>
        필터를 조정하거나 새 수리요청을 등록해보세요.
      </div>
      {onNew && (
        <button
          type="button"
          onClick={onNew}
          style={{
            marginTop: 6,
            padding: "10px 18px",
            borderRadius: 9999,
            background: C.greenDark,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
          }}
        >
          + 새 수리요청 등록
        </button>
      )}
    </div>
  );
}

// 실제 데이터 개수에 정확히 맞춰 동작하는 간단한 페이지네이션.
function SimplePagenation({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const btnBase = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    fontSize: 14,
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 20,
        margin: "24px 0",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        style={{
          ...btnBase,
          cursor: canPrev ? "pointer" : "default",
          color: canPrev ? C.sub : "#c3c3c3",
        }}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>이전</span>
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {numbers.map((n) => {
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: 9999,
                backgroundColor: active ? C.green : "transparent",
                color: active ? "#fff" : C.body,
                fontWeight: active ? 700 : 500,
                border: "none",
                transition: "all 0.15s ease",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        style={{
          ...btnBase,
          cursor: canNext ? "pointer" : "default",
          color: canNext ? C.sub : "#c3c3c3",
        }}
      >
        <span>다음</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

function MatchingMainScreen() {
  const { userData } = useUser();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const status = useMemo(() => searchParams.get("status") || null, [searchParams]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState([]); // 내 요청 전체 (필터/페이지 계산 소스)

  const [page, setPage] = useState(() => {
    if (navigationType === "POP") {
      return Number(sessionStorage.getItem(`page_${location.pathname}`) ?? 1);
    }
    return 1;
  });

  const myId = userData?.id;

  // 내 요청 전부를 한 번에 가져온다 — 실제 개수가 작다는 전제.
  // 백엔드 count 와 실제 목록이 불일치하는 문제를 회피하고, 페이지 수/필터/통계를
  // 로드된 데이터 기준으로 정확히 계산한다.
  const fetchAll = useCallback(async () => {
    if (!myId) return;
    setLoading(true);
    try {
      const list = await getMyMatchesApi(myId, { page: 1, limit: 500 });
      setAllItems(Array.isArray(list) ? list : []);
    } catch {
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [myId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // 필터 변경 시 첫 페이지로
  useEffect(() => {
    setPage(1);
  }, [status]);

  // 파생값: 필터 적용 후 리스트 + 페이지 슬라이스 + 통계
  const filtered = useMemo(() => {
    if (!status) return allItems;
    return allItems.filter((it) => String(it?.status) === String(status));
  }, [allItems, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentItems = useMemo(
    () => filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page],
  );

  const stats = useMemo(() => {
    const total = allItems.length;
    const inProgress = allItems.filter((i) => String(i?.status) === "0").length;
    const done = allItems.filter((i) => String(i?.status) === "1").length;
    return { total, inProgress, done };
  }, [allItems]);

  // 총 페이지 수보다 현재 페이지가 크면 보정
  useEffect(() => {
    if (page > totalPages) setPage(Math.max(1, totalPages));
  }, [totalPages, page]);

  useEffect(() => {
    sessionStorage.setItem(`page_${location.pathname}`, page);
    window.scrollTo({ top: 0 });
  }, [page, location.pathname]);

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            요청 목록
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            내가 등록한 수리요청을 확인하고 진행 상황을 관리하세요.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            background: C.greenDark,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          + 새 수리요청 등록
        </button>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatChip label="전체" value={stats.total} accent={C.ink} />
        <StatChip label="매칭중" value={stats.inProgress} accent={C.amber800} />
        <StatChip label="매칭완료" value={stats.done} accent={C.blue700} />
      </div>

      <FilterListMenu hideScope />

      <div
        className="list"
        style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}
      >
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : currentItems.length > 0 ? (
          currentItems.map((repair, index) => (
            <MatchingItem key={repair?.docId || index} repair={repair} />
          ))
        ) : (
          <EmptyState onNew={() => setIsDialogOpen(true)} />
        )}
      </div>

      <SimplePagenation page={page} totalPages={totalPages} onChange={setPage} />

      <RequestDialog open={isDialogOpen} setIsDialogOpen={setIsDialogOpen} />
    </div>
  );
}

export default MatchingMainScreen;
